import React, { useState } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, ShoppingBag, MapPin, Truck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHIPPING_FEE = 5.0;

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal } = useCart();
  const [submitting, setSubmitting] = useState(false);
  const [delivery, setDelivery] = useState("pickup");

  const shippingFee = delivery === "ship" ? SHIPPING_FEE : 0;
  const total = subtotal + shippingFee;

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        delivery_method: delivery,
      };
      const res = await axios.post(`${API}/checkout/session`, payload);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setSubmitting(false);
      }
    } catch (e) {
      console.error("Checkout error", e);
      toast.error("Checkout failed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col"
        style={{ backgroundColor: "var(--bg-primary)" }}
        data-testid="cart-drawer"
      >
        <SheetHeader
          className="px-6 py-5"
          style={{ borderBottom: "1px solid var(--border-soft)" }}
        >
          <SheetTitle
            className="font-display text-3xl text-left"
            style={{ color: "var(--text-primary)" }}
          >
            Your Cart
          </SheetTitle>
          <p
            className="font-script text-lg text-left"
            style={{ color: "var(--text-accent)" }}
          >
            {items.length === 0
              ? "ready when you are"
              : `${items.length} ${items.length === 1 ? "friend" : "friends"} waiting`}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div
              className="flex flex-col items-center justify-center text-center py-16"
              data-testid="cart-empty-state"
            >
              <ShoppingBag size={48} style={{ color: "var(--brand-peach)" }} />
              <p
                className="mt-4 text-base"
                style={{ color: "var(--text-secondary)" }}
              >
                Your cart is currently empty.
              </p>
              <p
                className="font-script text-2xl mt-2"
                style={{ color: "var(--text-accent)" }}
              >
                add a hug
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map(({ product, quantity }) => (
                <li
                  key={product.id}
                  className="flex gap-4 p-3 rounded-xl"
                  style={{
                    backgroundColor: "var(--bg-secondary)",
                    border: "1px solid var(--border-soft)",
                  }}
                  data-testid={`cart-item-${product.id}`}
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4
                      className="font-display text-lg leading-tight"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {product.name}
                    </h4>
                    <p
                      className="text-sm font-semibold mt-0.5"
                      style={{ color: "var(--brand-sage)" }}
                    >
                      ${product.price.toFixed(2)}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div
                        className="flex items-center rounded-full"
                        style={{
                          border: "1px solid var(--border-soft)",
                          backgroundColor: "#fff",
                        }}
                      >
                        <button
                          onClick={() => updateQty(product.id, quantity - 1)}
                          className="p-1.5"
                          style={{ color: "var(--text-primary)" }}
                          data-testid={`decrease-qty-${product.id}`}
                          aria-label="Decrease quantity"
                        >
                          <Minus size={14} />
                        </button>
                        <span
                          className="px-2 text-sm font-semibold min-w-[1.5rem] text-center"
                          data-testid={`qty-${product.id}`}
                        >
                          {quantity}
                        </span>
                        <button
                          onClick={() => updateQty(product.id, quantity + 1)}
                          className="p-1.5"
                          style={{ color: "var(--text-primary)" }}
                          data-testid={`increase-qty-${product.id}`}
                          aria-label="Increase quantity"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(product.id)}
                        className="p-1.5"
                        style={{ color: "var(--text-accent)" }}
                        data-testid={`remove-item-${product.id}`}
                        aria-label={`Remove ${product.name}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div
            className="px-6 py-5 space-y-4"
            style={{ borderTop: "1px solid var(--border-soft)" }}
          >
            <div
              className="rounded-xl overflow-hidden"
              style={{ border: "1px solid var(--border-soft)" }}
              data-testid="delivery-method-picker"
            >
              <button
                type="button"
                onClick={() => setDelivery("pickup")}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                style={{
                  backgroundColor: delivery === "pickup" ? "var(--brand-peach)" : "#fff",
                  color: "var(--text-primary)",
                }}
                data-testid="delivery-pickup-option"
              >
                <MapPin size={18} style={{ color: "var(--brand-sage)" }} />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Local pickup</div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    We’ll arrange a meet-up — free
                  </div>
                </div>
                <span className="text-sm font-semibold" style={{ color: "var(--brand-sage)" }}>FREE</span>
              </button>
              <button
                type="button"
                onClick={() => setDelivery("ship")}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                style={{
                  backgroundColor: delivery === "ship" ? "var(--brand-peach)" : "#fff",
                  color: "var(--text-primary)",
                  borderTop: "1px solid var(--border-soft)",
                }}
                data-testid="delivery-ship-option"
              >
                <Truck size={18} style={{ color: "var(--brand-sage)" }} />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Ship to me</div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    Mailed to your address
                  </div>
                </div>
                <span className="text-sm font-semibold" style={{ color: "var(--brand-sage)" }}>$5.00</span>
              </button>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm" style={{ color: "var(--text-secondary)" }}>
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-sm" style={{ color: "var(--text-secondary)" }}>
                <span>Shipping</span>
                <span data-testid="cart-shipping">{shippingFee === 0 ? "Free" : `$${shippingFee.toFixed(2)}`}</span>
              </div>
              <div
                className="flex items-center justify-between pt-2"
                style={{ borderTop: "1px solid var(--border-soft)" }}
              >
                <span className="font-display text-xl" style={{ color: "var(--text-primary)" }}>Total</span>
                <span className="font-display text-2xl" style={{ color: "var(--brand-sage)" }} data-testid="cart-total">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
            <p
              className="text-xs"
              style={{ color: "var(--text-secondary)" }}
            >
              Secure payment via Stripe.{delivery === "ship" && " Shipping address collected at checkout."}
            </p>
            <button
              onClick={handleCheckout}
              disabled={submitting}
              className="btn-sage w-full py-3.5 rounded-full text-sm font-semibold tracking-wide uppercase disabled:opacity-60"
              data-testid="checkout-button"
            >
              {submitting ? "Redirecting…" : "Checkout with Stripe"}
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
